import React, { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import styles from './InternshipForm.module.scss';
import { MAX_FILE_SIZE, LOADING_MS } from './constants';
import { getExt, formatFileSize } from './utils';
import { UploadIcon, DocIcon, Spinner, RemoveIcon } from './icons';

const FileField = ({
  id,
  label,
  accept,
  hint,
  dropText,
  errorNoun,
  validExts,
  onChange,
}) => {
  const [status, setStatus] = useState('idle'); // idle | loading | done | error
  const [file, setFile] = useState(null);
  const [error, setError] = useState('');
  const inputRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  const pickFile = (selected) => {
    if (!selected) return;
    clearTimeout(timerRef.current);

    const validFormat =
      validExts.includes(getExt(selected.name)) ||
      (errorNoun === 'видеовизитки' && selected.type.startsWith('video/'));

    if (!validFormat) {
      setStatus('error');
      setFile(null);
      setError(`Неверный формат для ${errorNoun}`);
      onChange(null);
      return;
    }
    if (selected.size > MAX_FILE_SIZE) {
      setStatus('error');
      setFile(null);
      setError(`Превышен максимально допустимый размер файла для ${errorNoun}`);
      onChange(null);
      return;
    }

    setError('');
    setFile(selected);
    setStatus('loading');
    timerRef.current = setTimeout(() => {
      setStatus('done');
      onChange(selected);
    }, LOADING_MS);
  };

  const handleChange = (e) => {
    pickFile(e.target.files[0]);
    e.target.value = '';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    pickFile(e.dataTransfer.files[0]);
  };

  const handleRemove = () => {
    clearTimeout(timerRef.current);
    setStatus('idle');
    setFile(null);
    setError('');
    onChange(null);
  };

  const showCard = status === 'loading' || status === 'done';

  return (
    <div className={styles.field}>
      <span className={styles.label}>{label}</span>

      {showCard ? (
        <div className={styles.fileCard}>
          <DocIcon />
          {status === 'loading' ? (
            <Spinner />
          ) : (
            <div className={styles.fileBody}>
              <div className={styles.fileTopRow}>
                <span className={styles.fileName}>{file.name}</span>
                <button
                  type="button"
                  className={styles.fileRemove}
                  onClick={handleRemove}
                  aria-label="Удалить файл"
                >
                  <RemoveIcon />
                </button>
              </div>
              <span className={styles.fileSize}>{formatFileSize(file.size)}</span>
            </div>
          )}
        </div>
      ) : (
        <label
          className={clsx(styles.dropzone, error && styles.dropzoneError)}
          htmlFor={id}
          onDragOver={(e) => e.preventDefault()}
          onDrop={handleDrop}
        >
          <input
            type="file"
            id={id}
            name={id}
            className={styles.fileInput}
            accept={accept}
            onChange={handleChange}
            ref={inputRef}
          />
          <UploadIcon />
          <div className={styles.dropzoneDesc}>
            <span className={styles.dropzoneText}>{dropText}</span>
            <span className={styles.dropzoneHint}>{hint}</span>
          </div>
        </label>
      )}

      {error && <span className={styles.errorText}>{error}</span>}
    </div>
  );
};

export default FileField;
